import dayjs from "dayjs";

import { useTimezone } from "@web-app/api";
import { Icon } from "@web-app/components";
import { TimeFormat } from "@web-app/types";
import { timeZoneMap } from "@web-app/utils/helpers";

import Tooltip from "../Tooltip";

export default function FlatDateTimeTz({
  start,
  end,
  userStart,
  userEnd,
  timezone,
  showIcon = true,
}: {
  end: Date;
  showIcon?: boolean;
  start: Date;
  timezone: keyof typeof timeZoneMap;
  userEnd: Date;
  userStart: Date;
}) {
  const { userTimezone } = useTimezone();

  if (timezone === "system") {
    return (
      <div className="relative">
        <div className="flex items-center justify-end w-[150px] min-w-[120px] cursor-pointer">
          {showIcon ? <Icon name="clock" className="mr-1" /> : <></>}
          <FormattedTime start={start} end={end} />
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <Tooltip
        tooltipClassName="w-[125px] flex justify-center"
        text={
          <FormattedTime
            start={userStart}
            end={userEnd}
            timezone={userTimezone}
          />
        }
      >
        <div className="flex items-center justify-end max-w-[150px] min-w-[120px] cursor-pointer">
          {showIcon ? <Icon name="clock" className="mr-1" /> : <></>}
          <FormattedTime start={start} end={end} timezone={timezone} />
        </div>
      </Tooltip>
    </div>
  );
}

const FormattedTime = ({
  start,
  end,
  timezone,
}: {
  end: Date;
  start: Date;
  timezone?: keyof typeof timeZoneMap;
}) => {
  const { getTimezone } = useTimezone();

  // Convert UTC times directly to formatted strings in the specified timezone
  const effectiveTimezone = getTimezone(timezone);

  const startFormatted =
    effectiveTimezone === "system"
      ? dayjs(start).format(TimeFormat.hour_minute)
      : dayjs.utc(start).tz(effectiveTimezone).format(TimeFormat.hour_minute);

  const endFormatted =
    effectiveTimezone === "system"
      ? dayjs(end).format(TimeFormat.hour_minute)
      : dayjs.utc(end).tz(effectiveTimezone).format(TimeFormat.hour_minute_tz);

  return (
    <span className="text-[10px] lg:text-xs">{`${startFormatted}-${endFormatted}`}</span>
  );
};
